import {useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {trpc, type RouterOutput} from '@common-web/lib/trpc';
import {useToast} from '@common-web/hooks/use-toast';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {PageHeader, PageHeaderAction} from '@common-web/components/page-header';
import {Badge} from '@common-web/components/ui/badge';
import {Button} from '@common-web/components/ui/button';
import {Skeleton} from '@common-web/components/ui/skeleton';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@common-web/components/ui/collapsible';
import {
  ArrowLeft,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Activity,
  FlaskConical,
  ShieldCheck,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Layers,
  Users,
  PauseCircle,
  Download,
  Loader2,
  FileText,
} from 'lucide-react';
import {format, formatDistanceToNow} from 'date-fns';

type RunMetadata = RouterOutput['internal']['vbaSyncCas']['getRunMetadata'];
type Report = RunMetadata['reports'][number];
type GroupMeta = Report['groups'][number];

const PAGE_SIZE = 200;

/** Classify a row by its `status` column, matching the router's error/warning tallies. */
function rowStatus(row: string[], statusIdx: number): 'error' | 'warning' | null {
  if (statusIdx === -1) return null;
  const s = (row[statusIdx] ?? '').toLowerCase();
  if (s === 'error') return 'error';
  if (s === 'warning' || s === 'warn') return 'warning';
  return null;
}

function ModeBadge({mode}: {mode: string}) {
  if (mode === 'COMMITTED') {
    return (
      <Badge className='bg-blue-100 text-blue-700 border-blue-200 font-medium'>
        <ShieldCheck className='h-3 w-3 mr-1' />
        Committed
      </Badge>
    );
  }
  if (mode === 'DRY_RUN') {
    return (
      <Badge className='bg-amber-100 text-amber-700 border-amber-200 font-medium'>
        <FlaskConical className='h-3 w-3 mr-1' />
        Dry Run
      </Badge>
    );
  }
  return <Badge className='bg-gray-100 text-gray-700 font-medium'>Unknown</Badge>;
}

function StatusBadge({status}: {status: string}) {
  if (status === 'FAILED') {
    return (
      <Badge className='bg-red-500 text-white border-0 font-medium'>
        <XCircle className='h-3 w-3 mr-1' />
        Failed
      </Badge>
    );
  }
  return (
    <Badge className='bg-emerald text-white border-0 font-medium'>
      <CheckCircle2 className='h-3 w-3 mr-1' />
      Completed
    </Badge>
  );
}

/**
 * Where a run's errors came from — group phase (chassis: contracts, plan menus, plan years) vs member
 * phase (enrollments and their plans).
 *
 * A chassis failure usually CAUSES the member failures under it, so collapsing both into one "Issues"
 * number buried the few rows that explained the many. Group-phase errors lead and stay visible even at
 * zero, because "0 group" is the reassuring half of the reading.
 *
 * Rendered only when the run's sidecar carries the split — runs summarized before it existed report
 * `undefined`, and showing those as 0 would claim a clean chassis the data can't vouch for.
 */
function PhaseErrorSplit({
  summary,
}: {
  summary: {groupPhaseErrors?: number; memberPhaseErrors?: number};
}) {
  const {groupPhaseErrors: group, memberPhaseErrors: member} = summary;
  if (group === undefined && member === undefined) return null;
  if (!group && !member) return null;
  return (
    <span className='flex items-center gap-2 tabular-nums'>
      <span className={group ? 'text-red-600 font-medium' : undefined}>
        {(group ?? 0).toLocaleString()} group
      </span>
      <span aria-hidden='true' className='text-brand-gray/50'>
        ·
      </span>
      <span className={member ? 'text-red-600' : undefined}>
        {(member ?? 0).toLocaleString()} member
      </span>
    </span>
  );
}

function SummaryStat({
  label,
  value,
  sub,
  icon,
  tone,
}: {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  icon?: React.ReactNode;
  tone?: 'red' | 'amber' | 'blue' | 'purple';
}) {
  const toneClass =
    tone === 'red'
      ? 'text-red-600'
      : tone === 'amber'
        ? 'text-amber'
        : tone === 'blue'
          ? 'text-brand-blue'
          : tone === 'purple'
            ? 'text-purple-600'
            : 'text-brand-navy';
  return (
    <div>
      <p className='text-sm text-brand-gray mb-1 flex items-center gap-1.5'>
        {icon}
        {label}
      </p>
      <p className={`text-lg font-bold ${toneClass}`}>{value}</p>
      {sub && <p className='text-xs text-brand-gray mt-0.5'>{sub}</p>}
    </div>
  );
}

/** Section-level button that fetches a presigned URL and downloads the report's raw CSV. */
function DownloadButton({s3Key}: {s3Key: string}) {
  const utils = trpc.useUtils();
  const {toast} = useToast();
  const [busy, setBusy] = useState(false);

  const download = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setBusy(true);
    try {
      const {url} = await utils.internal.vbaSyncCas.getDownloadUrl.fetch({key: s3Key});
      const a = document.createElement('a');
      a.href = url;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      toast({
        title: 'Download failed',
        description: err instanceof Error ? err.message : 'Could not generate a download link.',
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button size='sm' variant='outline' onClick={download} disabled={busy}>
      {busy ? (
        <Loader2 className='h-3.5 w-3.5 mr-1.5 animate-spin' />
      ) : (
        <Download className='h-3.5 w-3.5 mr-1.5' />
      )}
      Download CSV
    </Button>
  );
}

/** Table of a single page of a group's rows (loaded on demand). */
function GroupRowsTable({header, rows}: {header: string[]; rows: string[][]}) {
  const statusIdx = header.indexOf('status');
  return (
    <div className='overflow-x-auto border border-gray-100 rounded-md'>
      <table className='w-full text-sm border-collapse'>
        <thead>
          <tr className='bg-gray-50'>
            {header.map((h) => (
              <th
                key={h}
                className='text-left px-3 py-2 font-medium text-brand-gray whitespace-nowrap border-b border-gray-200'
              >
                {h.replace(/_/g, ' ')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => {
            const status = rowStatus(row, statusIdx);
            const rowClass =
              status === 'error'
                ? 'bg-red-50 border-b border-red-100'
                : status === 'warning'
                  ? 'bg-amber-50 border-b border-amber-100'
                  : 'border-b border-gray-100';
            const statusCellClass =
              status === 'error'
                ? 'text-red-600 font-medium'
                : status === 'warning'
                  ? 'text-amber-700 font-medium'
                  : 'text-brand-navy';
            return (
              <tr key={ri} className={rowClass}>
                {header.map((_, ci) => (
                  <td
                    key={ci}
                    className={`px-3 py-2 align-top ${
                      ci === statusIdx ? statusCellClass : 'text-brand-navy'
                    }`}
                  >
                    {row[ci] ?? ''}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** One group's collapsible block; rows load lazily (and page) only while expanded. */
function GroupBlock({
  runId,
  report,
  group,
  keyColumn,
}: {
  runId: string;
  report: Report;
  group: GroupMeta;
  keyColumn: string | null;
}) {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);

  const {data, isFetching} = trpc.internal.vbaSyncCas.getReportRows.useQuery(
    {runId, reportKey: report.key, groupKey: group.key, page, pageSize: PAGE_SIZE},
    {enabled: open, placeholderData: (prev) => prev},
  );

  const accent =
    group.errorCount > 0
      ? 'border-red-200 bg-red-50/30'
      : group.warningCount > 0
        ? 'border-amber-200 bg-amber-50/30'
        : 'border-gray-200';

  const total = data?.totalRows ?? group.rowCount;
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);
  const totalPages = data?.totalPages ?? Math.ceil(group.rowCount / PAGE_SIZE);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className={`border rounded-lg overflow-hidden ${accent}`}>
        <CollapsibleTrigger asChild>
          <button className='w-full px-4 py-2.5 flex items-center justify-between hover:bg-gray-50/50 transition-colors text-left'>
            <div className='flex items-center gap-3 min-w-0'>
              {open ? (
                <ChevronDown className='h-4 w-4 text-brand-gray shrink-0' />
              ) : (
                <ChevronRight className='h-4 w-4 text-brand-gray shrink-0' />
              )}
              <span className='font-medium text-brand-navy truncate'>
                {keyColumn ? `${keyColumn.replace(/_/g, ' ')}: ` : ''}
                {group.key}
              </span>
            </div>
            <div className='flex items-center gap-3 shrink-0'>
              {group.errorCount > 0 && (
                <span className='text-xs text-red-600 bg-red-100 px-2 py-0.5 rounded-full font-medium'>
                  {group.errorCount} errors
                </span>
              )}
              {group.warningCount > 0 && (
                <span className='text-xs text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full font-medium'>
                  {group.warningCount} warnings
                </span>
              )}
              <span className='text-xs font-mono text-brand-gray'>
                {group.rowCount.toLocaleString()} rows
              </span>
            </div>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className='border-t border-gray-100 p-3 space-y-3'>
            {!data ? (
              <Skeleton className='h-24 w-full' />
            ) : (
              <>
                <GroupRowsTable header={data.header} rows={data.rows} />
                {totalPages > 1 && (
                  <div className='flex items-center justify-between text-sm'>
                    <span className='text-brand-gray'>
                      Showing {from.toLocaleString()}–{to.toLocaleString()} of{' '}
                      {total.toLocaleString()}
                    </span>
                    <div className='flex items-center gap-2'>
                      {isFetching && <Loader2 className='h-4 w-4 animate-spin text-brand-gray' />}
                      <Button
                        size='sm'
                        variant='outline'
                        disabled={page <= 1 || isFetching}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                      >
                        Previous
                      </Button>
                      <Button
                        size='sm'
                        variant='outline'
                        disabled={page >= totalPages || isFetching}
                        onClick={() => setPage((p) => p + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

/** A top-level report section (Group Writes / Member Writes / On-Hold / Errors), grouped by group. */
function ReportSection({
  runId,
  report,
  icon,
}: {
  runId: string;
  report: Report;
  icon: React.ReactNode;
}) {
  const [open, setOpen] = useState(true);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card className='border-gray-200 shadow-sm'>
        <CardHeader className='pb-3'>
          <div className='flex items-center justify-between gap-3'>
            <CollapsibleTrigger asChild>
              <button className='flex items-center gap-2 text-left hover:opacity-80 transition-opacity min-w-0'>
                {open ? (
                  <ChevronDown className='h-4 w-4 text-brand-gray shrink-0' />
                ) : (
                  <ChevronRight className='h-4 w-4 text-brand-gray shrink-0' />
                )}
                {icon}
                <span className='text-base font-semibold text-brand-navy truncate'>
                  {report.label}
                </span>
                {report.mode && <ModeBadge mode={report.mode} />}
              </button>
            </CollapsibleTrigger>
            <div className='flex items-center gap-3 text-sm text-brand-gray shrink-0'>
              <span>
                {report.groups.length} {report.groupKeyColumn ? 'groups' : 'section'}
              </span>
              <span>{report.rowCount.toLocaleString()} rows</span>
              {report.errorCount > 0 && (
                <span className='text-red-500 font-medium flex items-center gap-1'>
                  <XCircle className='h-3 w-3' />
                  {report.errorCount} errors
                </span>
              )}
              {report.warningCount > 0 && (
                <span className='text-amber font-medium flex items-center gap-1'>
                  <AlertTriangle className='h-3 w-3' />
                  {report.warningCount} warnings
                </span>
              )}
              <DownloadButton s3Key={report.key} />
            </div>
          </div>
        </CardHeader>
        <CollapsibleContent>
          <CardContent className='pt-0 space-y-2'>
            {report.groups.length === 0 ? (
              <div className='text-sm text-brand-gray py-4 text-center'>No rows in this report</div>
            ) : (
              report.groups.map((g) => (
                <GroupBlock
                  key={g.key}
                  runId={runId}
                  report={report}
                  group={g}
                  keyColumn={report.groupKeyColumn}
                />
              ))
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

const SECTION_ICONS: Record<string, React.ReactNode> = {
  group_writes: <Layers className='h-4 w-4 text-brand-blue' />,
  member_writes: <Users className='h-4 w-4 text-purple-500' />,
  on_hold_members: <PauseCircle className='h-4 w-4 text-slate-500' />,
  errors: <XCircle className='h-4 w-4 text-red-500' />,
};

export function VbaSyncCasRunDetail() {
  const navigate = useNavigate();
  const {id = ''} = useParams<{id: string}>();

  const {data, isLoading, isFetching, refetch, error} =
    trpc.internal.vbaSyncCas.getRunMetadata.useQuery({id}, {enabled: !!id});

  if (error) {
    return (
      <div className='min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-blue/5 px-8 py-12'>
        <div className='max-w-3xl mx-auto text-center'>
          <XCircle className='h-10 w-10 text-red-500 mx-auto mb-3' />
          <h2 className='text-lg font-semibold text-brand-navy'>Could not load run</h2>
          <p className='text-brand-gray mt-1'>{error.message}</p>
          <Button variant='outline' className='mt-4' onClick={() => navigate('/vba-sync-cas')}>
            <ArrowLeft className='h-4 w-4 mr-2' />
            Back to runs
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-blue/5'>
      <div className='flex flex-col gap-0'>
        <PageHeader
          title={
            data ? `Run ${format(new Date(data.startedAt), 'MMM d, yyyy h:mm a')}` : 'Sync Run'
          }
          description={id}
          icon={<Activity className='h-6 w-6' />}
          isFetching={isFetching}
          isLoading={isLoading}
        >
          <PageHeaderAction tone='secondary' onClick={() => navigate('/vba-sync-cas')}>
            <ArrowLeft className='h-4 w-4 mr-2' />
            Back
          </PageHeaderAction>
          <PageHeaderAction tone='secondary' onClick={() => refetch()}>
            <RefreshCw className='h-4 w-4 mr-2' />
            Refresh
          </PageHeaderAction>
        </PageHeader>

        <div className='px-8 py-6 max-w-7xl mx-auto w-full flex flex-col gap-6'>
          {isLoading || !data ? (
            <>
              <Skeleton className='h-32 w-full' />
              <Skeleton className='h-64 w-full' />
            </>
          ) : (
            <>
              {/* Run summary */}
              <Card className='border-gray-200 shadow-sm'>
                <CardHeader className='bg-gray-50 border-b'>
                  <div className='flex items-center gap-3'>
                    <CardTitle className='text-lg font-bold text-brand-navy flex items-center gap-2'>
                      <Activity className='h-5 w-5' />
                      Sync Run
                    </CardTitle>
                    <ModeBadge mode={data.mode} />
                    <StatusBadge status={data.status} />
                  </div>
                </CardHeader>
                <CardContent className='p-6'>
                  <div className='grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-6'>
                    <SummaryStat
                      label='Started'
                      value={format(new Date(data.startedAt), 'MMM d, h:mm a')}
                      sub={formatDistanceToNow(new Date(data.startedAt), {addSuffix: true})}
                    />
                    <SummaryStat
                      label='Groups'
                      value={data.summary.groups.toLocaleString()}
                      icon={<Layers className='h-3.5 w-3.5 text-brand-blue' />}
                      tone='blue'
                    />
                    <SummaryStat
                      label='Members'
                      value={data.summary.members.toLocaleString()}
                      icon={<Users className='h-3.5 w-3.5 text-purple-500' />}
                      tone='purple'
                    />
                    <SummaryStat
                      label='Group Writes'
                      value={data.summary.groupRows.toLocaleString()}
                    />
                    <SummaryStat
                      label='Member Writes'
                      value={data.summary.memberRows.toLocaleString()}
                    />
                    <SummaryStat
                      label='On Hold'
                      value={data.summary.onHoldRows.toLocaleString()}
                      icon={<PauseCircle className='h-3.5 w-3.5 text-slate-500' />}
                    />
                    <SummaryStat
                      label='Issues'
                      value={
                        data.errorCount === 0 && data.warningCount === 0 ? (
                          <span className='flex items-center gap-1 text-emerald'>
                            <CheckCircle2 className='h-4 w-4' />
                            Clean
                          </span>
                        ) : (
                          <span className='flex items-center gap-3'>
                            {data.errorCount > 0 && (
                              <span className='flex items-center gap-1 text-red-600'>
                                <XCircle className='h-4 w-4' />
                                {data.errorCount}
                              </span>
                            )}
                            {data.warningCount > 0 && (
                              <span className='flex items-center gap-1 text-amber'>
                                <AlertTriangle className='h-4 w-4' />
                                {data.warningCount}
                              </span>
                            )}
                          </span>
                        )
                      }
                      sub={<PhaseErrorSplit summary={data.summary} />}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Report sections, each grouped by group; rows load on expand */}
              {data.reports.length === 0 ? (
                <div className='text-sm text-brand-gray py-8 text-center'>
                  This run produced no report files.
                </div>
              ) : (
                data.reports.map((report) => (
                  <ReportSection
                    key={report.key}
                    runId={data.id}
                    report={report}
                    icon={
                      SECTION_ICONS[report.name] ?? <FileText className='h-4 w-4 text-brand-gray' />
                    }
                  />
                ))
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
